import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  FileText, Star, Users, BarChart2, Heart,
  Plus, Trash2, Save, RefreshCw, Upload, Loader2, ImageIcon,
} from 'lucide-react';
import { getAboutContent, saveAboutContent, uploadTeamPhoto } from '../services/adminService';
import { toast } from 'sonner';

const TABS = [
  { key: 'story',   label: 'Our Story',   icon: FileText },
  { key: 'mission', label: 'Mission',      icon: Heart },
  { key: 'stats',   label: 'Stats',        icon: BarChart2 },
  { key: 'values',  label: 'Values',       icon: Star },
  { key: 'team',    label: 'Team',         icon: Users },
];

const inputCls = 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20';
const textareaCls = `${inputCls} w-full rounded-lg px-3 py-2 text-sm resize-none border outline-none focus:ring-2 transition-colors`;

const emptyContent = () => ({
  story: [],
  mission: '',
  stats: [],
  values: [],
  team: [],
});

export default function AboutContent() {
  const [tab, setTab] = useState('story');
  const [content, setContent] = useState(emptyContent());
  const [storyText, setStoryText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAboutContent();
      setContent(data);
      setStoryText((data.story || []).join('\n\n'));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const storyArray = storyText
        .split('\n\n')
        .map(p => p.trim())
        .filter(Boolean);
      await saveAboutContent({ ...content, story: storyArray });
      setContent(prev => ({ ...prev, story: storyArray }));
      toast.success('About page saved successfully.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Stats helpers ──
  const addStat = () => setContent(p => ({ ...p, stats: [...p.stats, { value: '', label: '', description: '' }] }));
  const removeStat = (i) => setContent(p => ({ ...p, stats: p.stats.filter((_, idx) => idx !== i) }));
  const updateStat = (i, field, val) => setContent(p => ({
    ...p, stats: p.stats.map((s, idx) => idx === i ? { ...s, [field]: val } : s),
  }));

  // ── Values helpers ──
  const addValue = () => setContent(p => ({ ...p, values: [...p.values, { title: '', description: '' }] }));
  const removeValue = (i) => setContent(p => ({ ...p, values: p.values.filter((_, idx) => idx !== i) }));
  const updateValue = (i, field, val) => setContent(p => ({
    ...p, values: p.values.map((v, idx) => idx === i ? { ...v, [field]: val } : v),
  }));

  // ── Team helpers ──
  const addMember = () => setContent(p => ({ ...p, team: [...p.team, { name: '', title: '', description: '', imageUrl: '' }] }));
  const removeMember = (i) => setContent(p => ({ ...p, team: p.team.filter((_, idx) => idx !== i) }));
  const updateMember = (i, field, val) => setContent(p => ({
    ...p, team: p.team.map((m, idx) => idx === i ? { ...m, [field]: val } : m),
  }));

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">About Page Content</h1>
          <p className="text-slate-400 text-sm mt-1">Edit all sections of the public about page</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={load} disabled={loading || saving}
            className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700">
            <RefreshCw className="h-4 w-4 mr-2" /> Reload
          </Button>
          <Button onClick={handleSave} disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {saving ? 'Saving…' : 'Save All'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/60 p-1 rounded-xl border border-slate-700 w-fit">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === key
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Story ── */}
      {tab === 'story' && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6 space-y-4">
            <div>
              <p className="text-white font-semibold mb-1">Our Story</p>
              <p className="text-slate-400 text-xs mb-3">
                Separate paragraphs with a blank line. Each block becomes one paragraph on the guest site.
              </p>
              <textarea
                rows={16}
                value={storyText}
                onChange={e => setStoryText(e.target.value)}
                placeholder={"Write your first paragraph here...\n\nLeave a blank line to start a new paragraph.\n\nEach block of text becomes its own paragraph on the about page."}
                className={textareaCls}
              />
              <p className="text-slate-500 text-xs mt-2">
                {storyText.split('\n\n').filter(p => p.trim()).length} paragraph(s)
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Mission ── */}
      {tab === 'mission' && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6 space-y-4">
            <div>
              <p className="text-white font-semibold mb-1">Our Mission</p>
              <p className="text-slate-400 text-xs mb-3">
                A single impactful statement displayed in the blue banner section.
              </p>
              <textarea
                rows={5}
                value={content.mission}
                onChange={e => setContent(p => ({ ...p, mission: e.target.value }))}
                placeholder="Describe the hotel's mission in one or two sentences..."
                className={textareaCls}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Stats ── */}
      {tab === 'stats' && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-white font-semibold">Stats / Highlights</p>
                <p className="text-slate-400 text-xs mt-0.5">Key numbers shown on the about page (e.g. 50+ Awards, 10,000+ Guests)</p>
              </div>
              <Button size="sm" onClick={addStat}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4 mr-1" /> Add Stat
              </Button>
            </div>

            {content.stats.length === 0 && (
              <div className="text-center py-10 text-slate-500">
                <BarChart2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No stats yet. Click "Add Stat" to add one.
              </div>
            )}

            <div className="space-y-3">
              {content.stats.map((stat, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.5fr_2.5fr_auto] gap-3 items-center">
                  <Input
                    value={stat.value}
                    onChange={e => updateStat(i, 'value', e.target.value)}
                    placeholder="50+"
                    className={inputCls}
                  />
                  <Input
                    value={stat.label}
                    onChange={e => updateStat(i, 'label', e.target.value)}
                    placeholder="Awards Won"
                    className={inputCls}
                  />
                  <Input
                    value={stat.description}
                    onChange={e => updateStat(i, 'description', e.target.value)}
                    placeholder="Recognition for excellence in hospitality"
                    className={inputCls}
                  />
                  <Button size="icon" variant="ghost" onClick={() => removeStat(i)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            {content.stats.length > 0 && (
              <p className="text-slate-500 text-xs pt-1">Columns: Value · Label · Description</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Values ── */}
      {tab === 'values' && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-white font-semibold">Our Values</p>
                <p className="text-slate-400 text-xs mt-0.5">Core values displayed as a card grid on the about page</p>
              </div>
              <Button size="sm" onClick={addValue}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4 mr-1" /> Add Value
              </Button>
            </div>

            {content.values.length === 0 && (
              <div className="text-center py-10 text-slate-500">
                <Star className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No values yet. Click "Add Value" to add one.
              </div>
            )}

            <div className="space-y-3">
              {content.values.map((val, i) => (
                <div key={i} className="grid grid-cols-[1.5fr_3fr_auto] gap-3 items-start">
                  <Input
                    value={val.title}
                    onChange={e => updateValue(i, 'title', e.target.value)}
                    placeholder="Excellence"
                    className={inputCls}
                  />
                  <textarea
                    rows={2}
                    value={val.description}
                    onChange={e => updateValue(i, 'description', e.target.value)}
                    placeholder="We strive for perfection in every aspect of our service..."
                    className={textareaCls}
                  />
                  <Button size="icon" variant="ghost" onClick={() => removeValue(i)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20 mt-0.5">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Team ── */}
      {tab === 'team' && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-white font-semibold">Leadership Team</p>
                <p className="text-slate-400 text-xs mt-0.5">Team members shown in the leadership section</p>
              </div>
              <Button size="sm" onClick={addMember}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4 mr-1" /> Add Member
              </Button>
            </div>

            {content.team.length === 0 && (
              <div className="text-center py-10 text-slate-500">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No team members yet. Click "Add Member" to add one.
              </div>
            )}

            <div className="space-y-4">
              {content.team.map((member, i) => (
                <TeamMemberRow
                  key={i}
                  member={member}
                  index={i}
                  onUpdate={updateMember}
                  onRemove={removeMember}
                  inputCls={inputCls}
                  textareaCls={textareaCls}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TeamMemberRow({ member, index, onUpdate, onRemove, inputCls, textareaCls }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadTeamPhoto(file);
      onUpdate(index, 'imageUrl', url);
      toast.success('Photo uploaded.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="bg-slate-700/40 border border-slate-600 rounded-xl p-4">
      <div className="flex gap-4 items-start">
        {/* Photo */}
        <div className="flex-shrink-0 flex flex-col items-center gap-2">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-600 border border-slate-500 flex items-center justify-center">
            {member.imageUrl
              ? <img src={member.imageUrl} alt={member.name} className="w-full h-full object-cover" />
              : <ImageIcon className="h-7 w-7 text-slate-400" />
            }
          </div>
          <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handlePhotoUpload} />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="border-slate-500 text-slate-300 hover:text-white hover:bg-slate-600 text-xs px-2 py-1 h-auto"
          >
            {uploading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Upload className="h-3 w-3 mr-1" />
            }
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </div>

        {/* Fields */}
        <div className="flex-1 grid grid-cols-2 gap-3">
          <Input
            value={member.name}
            onChange={e => onUpdate(index, 'name', e.target.value)}
            placeholder="Full Name"
            className={inputCls}
          />
          <Input
            value={member.title}
            onChange={e => onUpdate(index, 'title', e.target.value)}
            placeholder="Job Title"
            className={inputCls}
          />
          <textarea
            rows={3}
            value={member.description}
            onChange={e => onUpdate(index, 'description', e.target.value)}
            placeholder="Short bio..."
            className={`${textareaCls} col-span-2`}
          />
        </div>

        {/* Remove */}
        <Button size="icon" variant="ghost" onClick={() => onRemove(index)}
          className="text-red-400 hover:text-red-300 hover:bg-red-900/20 flex-shrink-0">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
